import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import MessagingConsentDialog from './MessagingConsentDialog';
import axios from 'axios';
import { logger } from '../utils/logger';
import { usePromptSlot } from '../utils/onboardingQueue';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const MessagingConsentWrapper = ({ children }) => {
  const { user, token } = useAuth();
  const [showConsent, setShowConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const consentActive = usePromptSlot('consent', showConsent);

  useEffect(() => {
    const checkConsentStatus = async () => {
      if (!token || !user) {
        logger.debug('No token or user, skipping consent check');
        setLoading(false);
        return;
      }

      // Skip consent prompts for users who haven't completed their initial
      // password change. Layering "consent modal" on top of "change your
      // temporary password" makes the first-login screen unreachable —
      // the user sees the consent wall, not the password form behind it.
      // Once they complete the password change, first_login flips to false
      // and this effect re-runs (token/user are dependencies), at which
      // point we check consent normally.
      if (user.first_login) {
        logger.debug('User in first_login state — deferring consent check');
        setLoading(false);
        return;
      }

      try {
        logger.debug('Checking consent status for user:', user.email);
        const response = await axios.get(`${API}/users/messaging-consent-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        logger.debug('Consent status response:', response.data);

        if (response.data.requires_consent) {
          logger.debug('User requires consent, showing dialog');
          setShowConsent(true);
        } else {
          logger.debug('User has already provided consent');
        }
      } catch (error) {
        console.error('Error checking consent status:', error);
        console.error('Error details:', error.response?.data || error.message);
        // Don't show consent dialog if there's an error checking status
      } finally {
        setLoading(false);
      }
    };

    checkConsentStatus();
  }, [token, user]);

  const handleConsent = async () => {
    try {
      logger.debug('Sending consent request...');
      const response = await axios.post(`${API}/users/messaging-consent`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      logger.debug('Consent recorded successfully:', response.data);

      // Update user in localStorage to reflect consent
      if (user) {
        const updatedUser = { ...user, messaging_consent: true, first_login: false };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      
      setShowConsent(false);
      
      // Force a page reload to ensure all components update
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('Error recording consent:', error);
      console.error('Error details:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to record consent: ${errorMessage}. Please try again.`);
      throw error; // Re-throw so the dialog knows it failed
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      {children}
      {showConsent && consentActive && (
        <MessagingConsentDialog 
          open={showConsent} 
          onConsent={handleConsent} 
        />
      )}
    </>
  );
};

export default MessagingConsentWrapper;
